import React, { useEffect, useState } from 'react';
import { Activity, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { getMyBodyRecords } from '../../lib/bodyRecords';
import { format, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';

function Stat({ label, value, unit, prev, higherIsBetter = false }) {
  const delta = value != null && prev != null ? value - prev : null;
  const isGood = delta == null ? null : higherIsBetter ? delta > 0 : delta < 0;
  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 text-center">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      {value != null ? (
        <>
          <p className="text-lg font-bold text-gray-900 dark:text-white">{value}<span className="text-xs font-normal text-gray-400 ml-0.5">{unit}</span></p>
          {delta != null && (
            <p className={`text-xs font-medium mt-0.5 flex items-center justify-center gap-0.5 ${isGood ? 'text-green-500' : delta === 0 ? 'text-gray-400' : 'text-red-500'}`}>
              {delta < 0 ? <TrendingDown className="w-3 h-3" /> : delta > 0 ? <TrendingUp className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
              {delta > 0 ? '+' : ''}{delta.toFixed(1)}
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-300 dark:text-gray-600">-</p>
      )}
    </div>
  );
}

export default function MyBody() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyBodyRecords()
      .then(data => setRecords(data))
      .catch(err => console.error('[MyBody] load error:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-4 text-center text-gray-400 text-sm pt-12">불러오는 중...</div>;

  if (records.length === 0) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">신체 정보</h2>
        <div className="card p-10 text-center">
          <Activity className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">기록된 신체 정보가 없습니다.</p>
          <p className="text-xs text-gray-400 mt-1">트레이너가 측정 후 입력해드립니다.</p>
        </div>
      </div>
    );
  }

  const latest = records[0];
  const prev = records[1];

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white">신체 정보</h2>

      {/* 최근 측정 요약 */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">최근 측정</p>
          <p className="text-xs text-gray-400">
            {latest.date ? format(parseISO(latest.date), 'M월 d일 (EEE)', { locale: ko }) : '-'}
            {latest.trainerName && <span> · {latest.trainerName} 트레이너</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Stat label="체중" value={latest.weight} unit="kg" prev={prev?.weight} />
          <Stat label="체지방" value={latest.bodyFat} unit="%" prev={prev?.bodyFat} />
          <Stat label="근육량" value={latest.muscleMass} unit="kg" prev={prev?.muscleMass} higherIsBetter />
        </div>
        {latest.notes && (
          <p className="text-xs text-gray-400 mt-3 italic">"{latest.notes}"</p>
        )}
      </div>

      {/* 전체 이력 */}
      <div>
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">측정 이력</p>
        <div className="space-y-2">
          {records.map((rec, idx) => {
            const nextRec = records[idx + 1];
            return (
              <div key={rec.id} className="card p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    {rec.date ? format(parseISO(rec.date), 'yyyy.MM.dd (EEE)', { locale: ko }) : '-'}
                  </p>
                  {rec.trainerName && (
                    <p className="text-xs text-gray-400">{rec.trainerName} 트레이너</p>
                  )}
                </div>
                <div className="flex gap-4 text-sm">
                  {rec.weight != null && (
                    <span>
                      <span className="text-xs text-gray-400">체중 </span>
                      <span className="font-medium">{rec.weight}kg</span>
                      {nextRec?.weight != null && (
                        <span className={`text-xs ml-1 ${rec.weight - nextRec.weight < 0 ? 'text-green-500' : rec.weight - nextRec.weight > 0 ? 'text-red-500' : 'text-gray-400'}`}>
                          ({rec.weight - nextRec.weight > 0 ? '+' : ''}{(rec.weight - nextRec.weight).toFixed(1)})
                        </span>
                      )}
                    </span>
                  )}
                  {rec.bodyFat != null && (
                    <span>
                      <span className="text-xs text-gray-400">체지방 </span>
                      <span className="font-medium">{rec.bodyFat}%</span>
                    </span>
                  )}
                  {rec.muscleMass != null && (
                    <span>
                      <span className="text-xs text-gray-400">근육 </span>
                      <span className="font-medium">{rec.muscleMass}kg</span>
                    </span>
                  )}
                </div>
                {rec.notes && <p className="text-xs text-gray-400 mt-1.5 italic">"{rec.notes}"</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
